import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Switch,
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Avatar,
    Stack,
    Chip,
    Divider,
    alpha,
    useTheme,
    CircularProgress,
    IconButton,
    Tabs,
    Tab,
    TextField,
    Alert,
} from '@mui/material';
import {
    AutoAwesome as MagicIcon,
    Forum as ForumIcon,
    Settings as SettingsIcon,
    CheckCircle as CheckCircleIcon,
    Shield as ShieldIcon,
    AccountBox as LeadIcon,
    SupportAgent as SupportIcon,
    Save as SaveIcon,
    Refresh as RefreshIcon,
    HourglassEmpty as HourglassIcon,
    Check as CheckIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Storefront as StorefrontIcon,
} from '@mui/icons-material';
import PageAlert from './PageAlert';
import { analyticsApi, automationApi } from '../services/api';
import ConnectPlatformsCTA from './ConnectPlatformsCTA';
import { useFeatureToggles } from '../contexts/FeatureToggleContext';
import { FEATURES } from '../constants/features';
import { io } from 'socket.io-client';
import { auth } from '../firebase';

const EngagementHub = () => {
    const theme = useTheme();
    const { isPlatformEnabled } = useFeatureToggles();
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);
    const hasAutoFetched = useRef(false);

    const [settings, setSettings] = useState({
        enabled: true,
        persona: 'professional',
        emoji_usage: 'occasionally',
        brand_vocabulary: '',
        response_language: 'English',
        auto_hide_offensive: true,
        lead_qualification: true,
        require_manual_approval: false,
        max_queue_time: 24,
        timeout_action: 'auto_send',
        // Brand voice
        brand_context: '',
        high_value_keywords: '',
        // CTAs
        cta_lead: '',
        cta_support: '',
        cta_general: '',
        // Platform toggles
        instagram_enabled: true,
        youtube_enabled: false,
    });

    const [interactions, setInteractions] = useState([]);
    const [pendingReplies, setPendingReplies] = useState([]);
    const [editingReply, setEditingReply] = useState(null); // { id: 'docId', text: 'new text' }

    // Unified error extraction
    const errorMsg = typeof error === 'string' ? error : (error?.message || '');
    const isNoAccountsError = !!errorMsg.toLowerCase().match(/no .*accounts|link.*platforms|social.*media.*setup/i);

    useEffect(() => {
        if (!hasAutoFetched.current) {
            hasAutoFetched.current = true;
            fetchSettings();
            fetchInteractions();
            fetchPendingReplies();
        }

        // Connect to Socket.IO backend
        const socket = io(process.env.REACT_APP_API_URL || 'http://127.0.0.1:5001');

        socket.on('connect', () => {
            console.log('EngagementHub connected to real-time stream');
            // Join the user's room so the server can target room-specific events (BUG-D fix)
            const uid = auth.currentUser?.uid;
            if (uid) {
                socket.emit('authenticate', { uid });
            }
        });

        socket.on('engagement_alert', (data) => {
            console.log('New Engagement Alert:', data);
            // Refresh data based on what changed
            if (data.type === 'pending_approval') {
                fetchPendingReplies();
            } else {
                fetchInteractions();
            }
        });

        socket.on('lead_alert', (data) => {
            console.log('Lead Follow-up Alert:', data);
            fetchInteractions();
        });

        return () => {
            socket.disconnect();
        };
    }, []);

    const fetchSettings = async () => {
        try {
            const data = await automationApi.getSettings();
            setSettings(data);
        } catch (err) {
            console.error('Failed to fetch settings', err);
            setError(err);
        }
    };

    const fetchInteractions = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await analyticsApi.getAll();

            if (data.error && data.error.includes("Firestore features will be disabled")) {
                setError("Firebase Service Account not configured. Automation features are running in limited mode.");
                setInteractions([]);
                return;
            }

            if (!Array.isArray(data)) {
                setInteractions([]);
                return;
            }

            setInteractions(data.filter(i => i.type === 'engagement'));
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPendingReplies = async () => {
        try {
            const data = await automationApi.getPendingReplies();
            setPendingReplies(data || []);
        } catch (err) {
            console.error('[EngagementHub] fetchPendingReplies failed', err);
        }
    };

    const handleSaveSettings = async () => {
        setSaving(true);
        try {
            await automationApi.updateSettings(settings);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            setError('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleApprove = async (docId) => {
        const textToSubmit = editingReply?.id === docId ? editingReply.text : null;
        try {
            await automationApi.approveReply(docId, textToSubmit);
            setEditingReply(null);
            fetchPendingReplies();
            fetchInteractions(); // Update activity feed
        } catch (err) {
            setError('Failed to approve reply');
        }
    };

    const handleReject = async (docId) => {
        try {
            await automationApi.rejectReply(docId);
            fetchPendingReplies();
        } catch (err) {
            setError('Failed to reject reply');
        }
    };

    if (isNoAccountsError) {
        return (
            <Box sx={{ py: 6 }}>
                <ConnectPlatformsCTA
                    title="Engagement Hub Locked"
                    description="Connect your social media platforms to unlock AI-powered automated replies, lead capture, and community management."
                    buttonText="Link Your Social Empire"
                />
            </Box>
        );
    }

    const renderSettings = () => (
        <Stack spacing={3}>
            <Card sx={{
                bgcolor: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(20px)',
                borderRadius: 4,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}>
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <MagicIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>AI Auto-Responder</Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Intelligent 24/7 engagement across your connected platforms
                                </Typography>
                            </Box>
                        </Box>
                        <Switch
                            checked={settings.enabled}
                            onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                            color="primary"
                        />
                    </Box>

                    {settings.enabled && (
                        <Stack spacing={1} sx={{ mb: 4, pl: 6 }}>
                            {isPlatformEnabled(FEATURES.ENGAGEMENT, 'instagram') && (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Instagram Comments</Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            Auto-reply to Instagram comments every 15 min
                                        </Typography>
                                    </Box>
                                    <Switch
                                        checked={settings.instagram_enabled !== false}
                                        onChange={(e) => setSettings({ ...settings, instagram_enabled: e.target.checked })}
                                        color="secondary"
                                    />
                                </Box>
                            )}
                            {isPlatformEnabled(FEATURES.ENGAGEMENT, 'youtube') && (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>YouTube Comments</Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            Poll your YouTube channel videos for new comments automatically
                                        </Typography>
                                    </Box>
                                    <Switch
                                        checked={settings.youtube_enabled || false}
                                        onChange={(e) => setSettings({ ...settings, youtube_enabled: e.target.checked })}
                                        color="error"
                                    />
                                </Box>
                            )}
                        </Stack>
                    )}

                    <Divider sx={{ my: 3 }} />

                    <Grid container spacing={4}>
                        <Grid xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ForumIcon fontSize="small" color="primary" /> Brand Voice
                            </Typography>
                            <Stack spacing={2}>
                                <FormControl fullWidth>
                                    <InputLabel id="talk-style-label">Reply Style</InputLabel>
                                    <Select
                                        labelId="talk-style-label"
                                        id="talk-style-select"
                                        value={settings.persona || 'professional'}
                                        label="Reply Style"
                                        onChange={(e) => setSettings({ ...settings, persona: e.target.value })}
                                    >
                                        <MenuItem value="professional">Professional & Direct</MenuItem>
                                        <MenuItem value="casual">Casual & Friendly</MenuItem>
                                        <MenuItem value="enthusiastic">High Energy / Hype</MenuItem>
                                        <MenuItem value="witty">Witty & Humorous</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth>
                                    <InputLabel id="emoji-label">Emoji Usage</InputLabel>
                                    <Select
                                        labelId="emoji-label"
                                        value={settings.emoji_usage || 'occasionally'}
                                        label="Emoji Usage"
                                        onChange={(e) => setSettings({ ...settings, emoji_usage: e.target.value })}
                                    >
                                        <MenuItem value="never">Never</MenuItem>
                                        <MenuItem value="occasionally">Occasionally</MenuItem>
                                        <MenuItem value="often">Often</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth>
                                    <InputLabel id="lang-label">Reply Language</InputLabel>
                                    <Select
                                        labelId="lang-label"
                                        value={settings.response_language || 'English'}
                                        label="Reply Language"
                                        onChange={(e) => setSettings({ ...settings, response_language: e.target.value })}
                                    >
                                        <MenuItem value="English">English</MenuItem>
                                        <MenuItem value="Spanish">Spanish</MenuItem>
                                        <MenuItem value="French">French</MenuItem>
                                        <MenuItem value="Portuguese">Portuguese</MenuItem>
                                        <MenuItem value="Arabic">Arabic</MenuItem>
                                        <MenuItem value="Hindi">Hindi</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Brand Phrases"
                                    placeholder="Words/phrases to always use (e.g. 'cheers', 'fam', 'drop us a DM')"
                                    value={settings.brand_vocabulary || ''}
                                    onChange={(e) => setSettings({ ...settings, brand_vocabulary: e.target.value })}
                                    helperText="The AI will weave these into replies when natural."
                                />
                            </Stack>
                        </Grid>

                        <Grid xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ShieldIcon fontSize="small" color="primary" /> Safety & Moderation
                            </Typography>
                            <Stack spacing={2}>
                                <Stack spacing={1}>
                                    <FormControlLabel
                                        control={<Switch checked={settings.auto_hide_offensive} onChange={(e) => setSettings({ ...settings, auto_hide_offensive: e.target.checked })} />}
                                        label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Auto-Hide Offensive Comments</Typography>}
                                    />
                                    <FormControlLabel
                                        control={<Switch checked={settings.lead_qualification} onChange={(e) => setSettings({ ...settings, lead_qualification: e.target.checked })} />}
                                        label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Automatic Lead Capture</Typography>}
                                    />
                                </Stack>

                                <Divider sx={{ opacity: 0.5 }} />

                                <Box>
                                    <FormControlLabel
                                        control={<Switch checked={settings.require_manual_approval} onChange={(e) => setSettings({ ...settings, require_manual_approval: e.target.checked })} color="warning" />}
                                        label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Require Manual Approval for Replies</Typography>}
                                    />
                                    {settings.require_manual_approval && (
                                        <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: 2, border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}` }}>
                                            <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.secondary', fontWeight: 500 }}>
                                                If you are offline, replies held in the queue will expire to prevent late responses. Choose how to handle expired queue items:
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid xs={12} sm={6}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Queue Timeout</InputLabel>
                                                        <Select
                                                            value={settings.max_queue_time}
                                                            label="Queue Timeout"
                                                            onChange={(e) => setSettings({ ...settings, max_queue_time: e.target.value })}
                                                        >
                                                            <MenuItem value={4}>4 Hours</MenuItem>
                                                            <MenuItem value={12}>12 Hours</MenuItem>
                                                            <MenuItem value={24}>24 Hours</MenuItem>
                                                            <MenuItem value={48}>48 Hours</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid xs={12} sm={6}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>On Timeout</InputLabel>
                                                        <Select
                                                            value={settings.timeout_action}
                                                            label="On Timeout"
                                                            onChange={(e) => setSettings({ ...settings, timeout_action: e.target.value })}
                                                        >
                                                            <MenuItem value="auto_send">Auto-Send AI Reply</MenuItem>
                                                            <MenuItem value="auto_drop">Delete (Do Not Send)</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}
                                </Box>
                            </Stack>
                        </Grid>
                        {/* New configuration for GAP-4, GAP-5, GAP-8 */}
                        <Grid xs={12}>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <StorefrontIcon fontSize="small" color="primary" /> Business Context & CTAs
                            </Typography>

                            <Grid container spacing={4}>
                                <Grid xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Brand Context & Offers"
                                        placeholder="What does your business sell? Current offers? E.g., 'We sell organic dog food. New customers get 20% off with code NEW20.'"
                                        value={settings.brand_context || ''}
                                        onChange={(e) => setSettings({ ...settings, brand_context: e.target.value })}
                                        helperText="Provide context so the AI can write relevant replies."
                                        sx={{ mb: 3 }}
                                    />

                                    <TextField
                                        fullWidth
                                        label="High-Value Keywords (comma separated)"
                                        placeholder="price, buy, order, where, how much"
                                        value={Array.isArray(settings.high_value_keywords) ? settings.high_value_keywords.join(', ') : settings.high_value_keywords || ''}
                                        onChange={(e) => setSettings({ ...settings, high_value_keywords: e.target.value.split(',').map(s => s.trim()) })}
                                        helperText="Words that indicate high buying intent. Triggers real-time alerts and DMs."
                                    />
                                </Grid>
                                <Grid xs={12} md={6}>
                                    <Stack spacing={2}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            label="Call-To-Action (Lead)"
                                            placeholder="DM us 'READY' for a quote!"
                                            value={settings.cta_lead || ''}
                                            onChange={(e) => setSettings({ ...settings, cta_lead: e.target.value })}
                                        />
                                        <TextField
                                            size="small"
                                            fullWidth
                                            label="Call-To-Action (Support)"
                                            placeholder="Please email support@example.com."
                                            value={settings.cta_support || ''}
                                            onChange={(e) => setSettings({ ...settings, cta_support: e.target.value })}
                                        />
                                        <TextField
                                            size="small"
                                            fullWidth
                                            label="Call-To-Action (General)"
                                            placeholder="Thanks for following! Link in bio."
                                            value={settings.cta_general || ''}
                                            onChange={(e) => setSettings({ ...settings, cta_general: e.target.value })}
                                        />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            onClick={handleSaveSettings}
                            disabled={saving}
                            sx={{ borderRadius: 3, px: 4, fontWeight: 700 }}
                        >
                            {saving ? 'Saving...' : 'Save Configuration'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* ── Brand Voice & CTAs ─────────────────────────────────────── */}
            <Card sx={{
                bgcolor: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(20px)',
                borderRadius: 4,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}>
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                        <ForumIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>Brand Voice & CTAs</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Control how the AI speaks, what it promotes, and which leads trigger alerts
                            </Typography>
                        </Box>
                    </Box>

                    <Grid container spacing={3}>
                        <Grid xs={12} sm={6} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Emoji Usage</InputLabel>
                                <Select
                                    value={settings.emoji_usage}
                                    label="Emoji Usage"
                                    onChange={(e) => setSettings({ ...settings, emoji_usage: e.target.value })}
                                >
                                    <MenuItem value="never">Never</MenuItem>
                                    <MenuItem value="occasionally">Occasionally</MenuItem>
                                    <MenuItem value="often">Often</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid xs={12} sm={6} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Reply Language</InputLabel>
                                <Select
                                    value={settings.response_language}
                                    label="Reply Language"
                                    onChange={(e) => setSettings({ ...settings, response_language: e.target.value })}
                                >
                                    <MenuItem value="English">English</MenuItem>
                                    <MenuItem value="Spanish">Spanish</MenuItem>
                                    <MenuItem value="French">French</MenuItem>
                                    <MenuItem value="Portuguese">Portuguese</MenuItem>
                                    <MenuItem value="Arabic">Arabic</MenuItem>
                                    <MenuItem value="Hindi">Hindi</MenuItem>
                                    <MenuItem value="German">German</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Brand Phrases"
                                placeholder="e.g. cheers, fam, let's go"
                                value={settings.brand_vocabulary}
                                onChange={(e) => setSettings({ ...settings, brand_vocabulary: e.target.value })}
                                helperText="Comma-separated words the AI should use"
                            />
                        </Grid>

                        <Grid xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                label="Brand Context"
                                placeholder="We are a fitness brand selling supplements. Our audience are gym-goers aged 18-35..."
                                value={settings.brand_context}
                                onChange={(e) => setSettings({ ...settings, brand_context: e.target.value })}
                                helperText="A short description of your business so the AI stays on-brand"
                            />
                        </Grid>

                        <Grid xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="High-Value Keywords"
                                placeholder="price, buy, how much, cost"
                                value={settings.high_value_keywords}
                                onChange={(e) => setSettings({ ...settings, high_value_keywords: e.target.value })}
                                helperText="Comma-separated — triggers lead alert emails"
                            />
                        </Grid>

                        <Grid xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Lead CTA"
                                placeholder="DM us or visit our website to order!"
                                value={settings.cta_lead}
                                onChange={(e) => setSettings({ ...settings, cta_lead: e.target.value })}
                                helperText="Guidance for purchase / pricing comments"
                            />
                        </Grid>

                        <Grid xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Support CTA"
                                placeholder="Please DM us and we'll sort it out!"
                                value={settings.cta_support}
                                onChange={(e) => setSettings({ ...settings, cta_support: e.target.value })}
                                helperText="Guidance for complaints / issues"
                            />
                        </Grid>

                        <Grid xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="General CTA"
                                placeholder="Thanks for the love! Stay tuned for more."
                                value={settings.cta_general}
                                onChange={(e) => setSettings({ ...settings, cta_general: e.target.value })}
                                helperText="Guidance for praise / casual comments"
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                        Platform Toggles
                    </Typography>
                    <Stack spacing={1}>
                        {isPlatformEnabled(FEATURES.ENGAGEMENT, 'instagram') && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.instagram_enabled !== false}
                                        onChange={(e) => setSettings({ ...settings, instagram_enabled: e.target.checked })}
                                        color="secondary"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Instagram Comment Automation</Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            Auto-reply to Instagram comments every 15 min (on by default)
                                        </Typography>
                                    </Box>
                                }
                            />
                        )}
                        {isPlatformEnabled(FEATURES.ENGAGEMENT, 'youtube') && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.youtube_enabled}
                                        onChange={(e) => setSettings({ ...settings, youtube_enabled: e.target.checked })}
                                        color="error"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>YouTube Comment Automation</Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            Polls your YouTube channel every 30 min for new comments
                                        </Typography>
                                    </Box>
                                }
                            />
                        )}
                    </Stack>

                    {saved && (
                        <Alert severity="success" sx={{ mt: 3, borderRadius: 3 }}>
                            Settings updated successfully
                        </Alert>
                    )}

                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            onClick={handleSaveSettings}
                            disabled={saving}
                            sx={{ borderRadius: 3, px: 4, fontWeight: 700 }}
                        >
                            {saving ? 'Saving...' : 'Save Brand Voice'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Stack>
    );

    const renderInteractions = () => (
        <Stack spacing={2}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress />
                </Box>
            ) : interactions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 10, bgcolor: alpha(theme.palette.background.paper, 0.4), borderRadius: 4 }}>
                    <ForumIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        No high-value interactions detected yet. AI is monitoring your profile...
                    </Typography>
                </Box>
            ) : (
                interactions.map((item, idx) => (
                    <Card key={idx} sx={{
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                        '&:hover': { bgcolor: alpha(theme.palette.background.paper, 0.8) }
                    }}>
                        <CardContent sx={{ display: 'flex', gap: 2 }}>
                            <Avatar sx={{ bgcolor: item.intent === 'lead' ? 'success.main' : 'info.main' }}>
                                {item.intent === 'lead' ? <LeadIcon /> : <SupportIcon />}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        {item.intent === 'lead' ? 'New Potential Lead' : 'Customer Support Inquiry'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {item.platform && (
                                            <Chip
                                                label={item.platform}
                                                size="small"
                                                color={item.platform === 'instagram' ? 'secondary' : item.platform === 'youtube' ? 'error' : 'default'}
                                                sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }}
                                            />
                                        )}
                                        <Chip
                                            label={item.intent.toUpperCase()}
                                            size="small"
                                            color={item.intent === 'lead' ? 'success' : 'info'}
                                            sx={{ fontWeight: 700, height: 20, fontSize: '0.65rem' }}
                                        />
                                    </Box>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1.5, p: 1.5, bgcolor: alpha(theme.palette.text.primary, 0.03), borderRadius: 2 }}>
                                    "{item.comment_text}"
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                                    <CheckCircleIcon sx={{ fontSize: 16 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                        AI Replied: {String(item.ai_response || '')}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                ))
            )}
        </Stack>
    );

    const renderModerationQueue = () => (
        <Stack spacing={2}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress />
                </Box>
            ) : pendingReplies.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 10, bgcolor: alpha(theme.palette.background.paper, 0.4), borderRadius: 4 }}>
                    <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2, opacity: 0.8 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>All Caught Up!</Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        There are no pending AI replies waiting for your approval.
                    </Typography>
                </Box>
            ) : (
                pendingReplies.map((item, idx) => (
                    <Card key={idx} sx={{
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                        bgcolor: alpha(theme.palette.warning.main, 0.02),
                        position: 'relative',
                        overflow: 'visible'
                    }}>
                        <Box sx={{
                            position: 'absolute', top: -12, right: 24, bgcolor: 'warning.main', color: 'warning.contrastText',
                            px: 2, py: 0.5, borderRadius: 4, fontSize: '0.75rem', fontWeight: 800,
                            display: 'flex', alignItems: 'center', gap: 0.5
                        }}>
                            <HourglassIcon sx={{ fontSize: 14 }} /> Pending Approval
                        </Box>

                        {item.is_high_value && (
                            <Box sx={{
                                position: 'absolute', top: -12, left: 24, bgcolor: 'warning.main', color: 'warning.contrastText',
                                px: 2, py: 0.5, borderRadius: 4, fontSize: '0.75rem', fontWeight: 800,
                                display: 'flex', alignItems: 'center', gap: 0.5
                            }}>
                                <MagicIcon sx={{ fontSize: 14 }} /> HIGH VALUE
                            </Box>
                        )}

                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                <Avatar sx={{ bgcolor: item.intent === 'lead' ? 'success.main' : 'info.main' }}>
                                    {item.intent === 'lead' ? <LeadIcon /> : <SupportIcon />}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            {item.intent === 'lead' ? 'Potential Lead' : 'Customer Comment'}
                                        </Typography>
                                        <Chip
                            label={item.platform || 'unknown'}
                            size="small"
                            color={item.platform === 'instagram' ? 'secondary' : item.platform === 'youtube' ? 'error' : 'default'}
                            sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }}
                        />
                                    </Box>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        "{item.comment_text}"
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                                        Received {
                                            (() => {
                                                const d = item.createdAt?.toDate ? item.createdAt.toDate()
                                                    : item.createdAt?.seconds ? new Date(item.createdAt.seconds * 1000)
                                                        : new Date(item.createdAt);
                                                return isNaN(d?.getTime()) ? 'Unknown' : d.toLocaleString();
                                            })()
                                        }
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ bgcolor: alpha(theme.palette.background.paper, 0.8), p: 2, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <MagicIcon sx={{ fontSize: 14 }} /> AI Proposed Reply
                                </Typography>

                                {editingReply?.id === item.id ? (
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        value={editingReply.text}
                                        onChange={(e) => setEditingReply({ ...editingReply, text: e.target.value })}
                                        variant="outlined"
                                        size="small"
                                        sx={{ mb: 2, bgcolor: theme.palette.background.paper }}
                                    />
                                ) : (
                                    <Typography variant="body2" sx={{ mb: 2, p: 1.5, bgcolor: alpha(theme.palette.background.default, 0.5), borderRadius: 1 }}>
                                        {String(item.ai_response || '')}
                                    </Typography>
                                )}

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                    <Button
                                        size="small"
                                        color="error"
                                        onClick={() => handleReject(item.id)}
                                        startIcon={<DeleteIcon />}
                                    >
                                        Reject
                                    </Button>

                                    {editingReply?.id === item.id ? (
                                        <Button
                                            size="small"
                                            color="inherit"
                                            onClick={() => setEditingReply(null)}
                                        >
                                            Cancel Options
                                        </Button>
                                    ) : (
                                        <Button
                                            size="small"
                                            color="inherit"
                                            onClick={() => setEditingReply({ id: item.id, text: item.ai_response })}
                                            startIcon={<EditIcon />}
                                        >
                                            Edit
                                        </Button>
                                    )}

                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleApprove(item.id)}
                                        startIcon={<CheckIcon />}
                                        sx={{ fontWeight: 700 }}
                                    >
                                        {editingReply?.id === item.id ? 'Save & Send' : 'Approve & Send'}
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                ))
            )}
        </Stack>
    );

    return (
        <Box>
            <Box sx={{ mb: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="h3" sx={{ fontWeight: 900, mb: 1 }}>
                        Engagement Hub
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Monitor and automate your business conversations
                    </Typography>
                </Box>
                <IconButton onClick={fetchInteractions} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}>
                    <RefreshIcon />
                </IconButton>
            </Box>

            <PageAlert error={error} />

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ mb: 4, '& .MuiTab-root': { fontWeight: 800, textTransform: 'none', fontSize: '1rem' } }}
            >
                <Tab icon={<SettingsIcon sx={{ mr: 1 }} />} iconPosition="start" label="Automation Settings" />
                <Tab
                    icon={
                        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <HourglassIcon sx={{ mr: 1 }} />
                            {pendingReplies.length > 0 && (
                                <Box sx={{
                                    position: 'absolute', top: -8, left: -8,
                                    bgcolor: 'error.main', color: 'white',
                                    borderRadius: '50%', width: 16, height: 16,
                                    fontSize: '0.65rem', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    fontWeight: 'bold'
                                }}>
                                    {pendingReplies.length}
                                </Box>
                            )}
                        </Box>
                    }
                    iconPosition="start"
                    label="Moderation Queue"
                />
                <Tab icon={<ForumIcon sx={{ mr: 1 }} />} iconPosition="start" label="AI Activity Feed" />
            </Tabs>

            {tab === 0 && renderSettings()}
            {tab === 1 && renderModerationQueue()}
            {tab === 2 && renderInteractions()}
        </Box>
    );
};

export default EngagementHub;
